import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { Button, DataTable, type ColumnDef, SectionCard } from "@/deps/ui-library";
import { useCompanyProfile, useReceipt } from "@/api/queries";
import type { Receipt } from "@/api/types";
import { DocumentScreen } from "@/components/DocumentScreen";
import { PrintOverlay } from "@/components/print/PrintOverlay";
import { ReceiptPrint } from "@/components/print/ReceiptPrint";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

type Application = Receipt["applications"][number];

const APPLIED_COLUMNS: Array<ColumnDef<Application>> = [
  {
    id: "invoice",
    header: "Invoice",
    cell: ({ row: { original: application } }) => (
      <span className="num">{application.invoiceNumber}</span>
    ),
  },
  {
    id: "balance",
    header: "Balance was",
    meta: { textAlign: "right" },
    cell: ({ row: { original: application } }) => (
      <span className="num text-muted-foreground">{formatAmount(application.invoiceBalance)}</span>
    ),
  },
  {
    id: "applied",
    header: "Applied",
    meta: { textAlign: "right" },
    cell: ({ row: { original: application } }) => (
      <span className="num">{formatAmount(application.applied)}</span>
    ),
  },
  {
    id: "withheld",
    header: "Tax withheld",
    meta: { textAlign: "right" },
    cell: ({ row: { original: application } }) => (
      <span className="num text-muted-foreground">{formatAmount(application.tdsWithheld)}</span>
    ),
  },
];

export function ReceiptDetailPage() {
  const { id } = useParams({ from: "/sales/receipts/$id" });
  const { data, isPending, error } = useReceipt(id);
  const { data: seller } = useCompanyProfile();
  const [printing, setPrinting] = useState(false);

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading receipt…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That receipt could not be loaded.</p>;

  const applied = data.applications.reduce((total, a) => total + Number(a.applied), 0);

  return (
    <>
      <PrintOverlay
        open={printing}
        onClose={() => setPrinting(false)}
        title={`Receipt ${data.number ?? ""}`.trim()}
      >
        <ReceiptPrint receipt={data} seller={seller} />
      </PrintOverlay>
      <DocumentScreen
        title="Receipt"
        number={data.number}
        status={data.status}
        locked={data.status === "approved"}
        subtitle={`${data.customerName} · ${formatDate(data.date)}`}
        actions={
          <span className="flex items-center gap-2">
            {data.status === "approved" ? (
              <DownloadPdfButton kind="receipt" id={data.id} number={data.number} />
            ) : null}
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() => setPrinting(true)}
            >
              Print
            </Button>
          </span>
        }
        facts={[
          { label: "Customer", value: data.customerName },
          { label: "Received", value: formatDate(data.date) },
          { label: "Method", value: data.method },
          { label: "Into", value: data.bankAccountName },
          { label: "Amount", value: <span className="num">{formatAmount(data.amount)}</span> },
        ]}
        history={data.history}
      >
        <SectionCard title="Applied to" flush>
          {data.applications.length === 0 ? (
            <p className="text-fg-muted p-4 text-sm">
              Nothing was settled by this receipt. The whole amount is held as a customer advance.
            </p>
          ) : (
            <DataTable
              aria-label="Invoices settled by this receipt"
              size="sm"
              enableSorting={false}
              columns={APPLIED_COLUMNS}
              data={data.applications}
              getRowId={(application) => application.invoiceId}
              pagination={{ mode: "manual", show: false }}
            />
          )}
        </SectionCard>

        {Number(data.advance) > 0 ? (
          <SectionCard title="Held as advance">
            {/* Money received before there is anything to settle is held against the customer, not
              lost and not guessed at an invoice. RULE 4.2 is the same idea from the other side. */}
            <p className="text-fg text-sm">
              <span className="num font-semibold">{formatAmount(data.advance)}</span> of this
              receipt is still held for {data.customerName}. It can be used from any of their open
              invoices.
            </p>
          </SectionCard>
        ) : null}

        <p className="text-fg-muted text-xs">
          Applied <span className="num">{formatAmount(applied.toFixed(2))}</span> of{" "}
          <span className="num">{formatAmount(data.amount)}</span> received.
        </p>
      </DocumentScreen>
    </>
  );
}
